/* ============================================================
   PITBULL — I'M BACK TOUR — theme.js
   Vanilla JS: FAQ, event info tabs, mobile menu, AJAX cart,
   reveal on scroll

   ── STRINGS ──
   This store is English only. Liquid renders the copy, and nothing
   rewrites it after load.

   Elements that JS creates or relabels at runtime (cart lines, the
   seat-map legend, button states) carry  data-i18n="key"  and look
   the text up in the `strings` dictionary below. That is the only
   remaining indirection, and it exists because those strings have
   no Liquid to come from.

   `data-i18n-attr="placeholder|aria-label|alt"` sets an attribute
   instead of the element's text.

   There used to be a second pattern: every piece of merchant copy
   carried both languages inline as data-i18n-fr / data-i18n-en, and
   a pass on load picked one. It was removed -- a phone recording
   showed the page rendering "OBTENIR DES BILLETS" and flipping to
   "GET TICKETS" a beat later, because Liquid emitted the French
   value and JS corrected it afterwards. Rendering the right text
   once is strictly better than rendering the wrong text and fixing
   it.
   ============================================================ */

(function () {
  'use strict';

  /* ── Language ── */
  /* One language. DEFAULT_LANG and FALLBACK_LANG are kept because `t()` and
     `setLang()` still read them, but there is nothing to switch to: the French
     dictionary, the inline fr/en attributes, the switcher and locales/fr.json
     are all gone. The stale sessionStorage key is still cleared on the way past
     so an old value cannot pin a returning visitor to a language that no longer
     exists. */
  var STORAGE_KEY = 'orelsan_lang';
  var DEFAULT_LANG = 'en';
  var FALLBACK_LANG = 'en';
  var currentLang = DEFAULT_LANG;

  var strings = {
    en: {
      'nav-home': 'Home', 'nav-tour': 'Tour', 'nav-info': 'Info',
      'nav-faq': 'FAQ', 'nav-contact': 'Contact',
      'nav-home-aria': 'Home', 'nav-main-aria': 'Main navigation',
      'cart-aria': 'Cart', 'menu-aria': 'Menu',
      'menu-open-aria': 'Open menu', 'menu-close-aria': 'Close menu',
      'cart-label': 'Cart',
      'hero-cta': 'GET TICKETS →',
      'hero-badge': 'LIMITED TICKETS', 'hero-badge-br': '★ SELLING FAST',
      'hero-eyebrow': 'ACCOR ARENA // PARIS // 9 · 14 · 15 DEC 2026',
      'hero-trust-1': 'OFFICIAL TICKETS', 'hero-trust-2': 'SECURE CHECKOUT', 'hero-trust-3': 'QR CODE DELIVERY',
      'tickets-title': 'GET YOUR TICKETS',
      'per-ticket': 'per ticket // EUR', 'add-to-cart': 'ADD TO CART',
      'product-quantity': 'Quantity', 'product-qty-down': 'Decrease quantity',
      'product-qty-up': 'Increase quantity',
      'sold-out': 'SOLD OUT', 'best-badge': 'BEST',
      'price-tba': 'Price to be announced', 'on-sale-soon': 'ON SALE SOON',
      'selling-fast': 'SELLING FAST', 'almost-gone': 'ALMOST GONE',
      'few-left': 'FEW LEFT',
      'show-starts': 'SHOW STARTS',
      'cd-days': 'Days', 'cd-hours': 'Hours', 'cd-mins': 'Min', 'cd-secs': 'Sec',
      'info-title': 'EVENT INFO', 'info-venue-label': 'Venue', 'info-addr-label': 'Address',
      'info-map': 'VIEW ON MAP →', 'info-doors-label': 'Doors Open',
      'info-show-label': 'Show Starts', 'info-age-label': 'Age Policy',
      'info-prev': '← PREVIOUS', 'info-next': 'NEXT →',
      'info-prev-aria': 'View the previous date', 'info-next-aria': 'View the next date',
      'tour-title': 'TOUR DATES', 'tour-btn': 'TICKETS →',
      'hiw-title': 'HOW IT WORKS',
      'faq-title': 'FAQ',
      'cart-tier-label': 'Category',
      'footer-nav-title': 'Navigate', 'footer-link1': 'Tickets', 'footer-link2': 'Event Info',
      'footer-tos': 'Terms of Service', 'footer-privacy-link': 'Privacy Policy',
      'footer-refund': 'Refund Policy',
      'footer-nl-title': 'Stay Updated', 'footer-nl-body': 'Get notified about future shows.',
      'footer-nl-placeholder': 'Your email', 'footer-nl-btn': 'GO', 'footer-nl-aria': 'Email address',
      'footer-contact-label': 'Contact:',
      'footer-privacy': 'Privacy', 'footer-terms': 'Terms',
      'cart-title': 'Your cart', 'cart-empty': 'Your cart is empty',
      'cart-get-tickets': 'Get tickets', 'cart-remove': 'Remove',
      'cart-summary': 'Order summary', 'cart-total': 'Total',
      'cart-checkout': 'Checkout →', 'cart-continue': '← Continue shopping',
      'product-select': 'Select option', 'product-add': 'Add to cart',
      'product-sold-out': 'Sold out',
      'collection-empty': 'No tickets available yet.',
      '404-title': 'Page not found', '404-body': "The page you were looking for doesn't exist.",
      '404-back': 'Back to tickets',
      'seatmap-title': "SEATING MAP",
      'seatmap-dates-aria': "Choose a date",
      'seatmap-svg-aria': "Seating plan: floor, lower bowl and upper bowl",
      'seatmap-floor': "FLOOR",
      'seatmap-stage': "STAGE",
      'seatmap-selected': "SELECTED ZONE",
      'seatmap-from': "From",
      'seatmap-cta': "VIEW TICKETS →",
      'seatmap-disclaimer': "Indicative plan // your exact seat is printed on your ticket",
      'seatmap-hint': "Drag the plan to explore",
      'seatmap-viewport-aria': "Seating plan — scrolls sideways",
      'state-adding': 'Adding…', 'state-added': '★ Added', 'state-retry': 'Try again',
      'drawer-title': 'Your cart', 'drawer-close': 'Close',
      'drawer-empty': 'Your cart is empty', 'drawer-subtotal': 'Subtotal',
      'drawer-checkout': 'Checkout →', 'drawer-view-cart': 'View cart',
      'drawer-addons': 'Secure checkout · Ticket emailed before the show',
      'drawer-remove': 'Remove',
      'drawer-qty-down': 'Decrease quantity', 'drawer-qty-up': 'Increase quantity',
      'reassure-1-title': "TICKETS SENT BY EMAIL",
      'reassure-1-body': "Your QR code arrives by email within minutes of payment. Nothing to print, nothing to wait for in the post.",
      'reassure-2-title': "SECURE PAYMENT",
      'reassure-2-body': "Card, PayPal or Klarna, handled by Shopify. Your card details never pass through us.",
      'reassure-3-title': "NOT SURE? WRITE TO US",
      'reassure-3-body': "We answer before you buy, and after →",
      'board-date': 'DATE', 'board-city': 'CITY', 'board-venue': 'VENUE',
      'board-next': 'NEXT', 'board-gone': 'SOLD OUT', 'board-from': 'FROM',
      'board-tickets': 'TICKETS',
      'search-title': 'Search', 'search-placeholder': 'Search',
      'search-submit': 'Search', 'search-aria': 'Search',
      'search-none': 'Nothing found for', 'search-count': 'results',
      'search-back': 'See the dates'
    }
  };

  function t(key) {
    var table = strings[currentLang] || strings[FALLBACK_LANG];
    if (Object.prototype.hasOwnProperty.call(table, key)) return table[key];
    // Never render a raw key: leave whatever is already on screen.
    if (Object.prototype.hasOwnProperty.call(strings[FALLBACK_LANG], key)) return strings[FALLBACK_LANG][key];
    return null;
  }

  function apply(el, value) {
    if (value === null || value === undefined) return;
    var attr = el.getAttribute('data-i18n-attr');
    if (attr) { el.setAttribute(attr, value); }
    else { el.innerHTML = value; }
  }


  function setLang(lang) {
    lang = DEFAULT_LANG;                     // one language; the argument is vestigial
    currentLang = lang;
    window.currentLang = lang;
    try { sessionStorage.setItem(STORAGE_KEY, lang); } catch (e) { /* private mode */ }

    document.documentElement.lang = lang;

    // 1. Fixed chrome, from the dictionary above.
    document.querySelectorAll('[data-i18n]').forEach(function (el) {
      apply(el, t(el.getAttribute('data-i18n')));
    });

    /* The second pass is gone with the French. Merchant copy used to be carried
       on the element as a data-i18n-fr / data-i18n-en pair and swapped here,
       which is what produced the visible "OBTENIR DES BILLETS" -> "GET TICKETS"
       flip on load. Liquid now renders the English directly and nothing
       rewrites it afterwards. The page title comes from the document. */

    document.dispatchEvent(new CustomEvent('lang:change', { detail: { lang: lang } }));
  }

  function storedLang() {
    /* With one language and no switcher there is nothing to remember, and a
       value left in sessionStorage from before the switcher was removed would
       otherwise pin the page to French forever with no way for the visitor to
       undo it. Always the default now; the stale key is cleared on the way
       past so nobody inherits it. */
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* private mode */ }
    return DEFAULT_LANG;
  }

  /* ── Event info tabs ── */
  var currentInfoTab = 0;
  var infoAnimating = false;

  function infoTabCount() {
    return document.querySelectorAll('[id^="info-panel-"]').length;
  }

  // direction is optional — worked out from the current tab when omitted,
  // so inline handlers never have to track state themselves.
  function setInfoTab(index, direction) {
    if (infoAnimating || index === currentInfoTab) return;
    var current = document.getElementById('info-panel-' + currentInfoTab);
    var next = document.getElementById('info-panel-' + index);
    if (!current || !next) return;

    infoAnimating = true;
    var fromRight = direction ? direction === 'next' : index > currentInfoTab;

    next.style.display = 'grid';
    next.style.position = 'absolute';
    next.style.top = current.offsetTop + 'px';
    next.style.left = '0';
    next.style.width = '100%';
    next.style.transition = 'none';
    next.style.transform = fromRight ? 'translateX(100%)' : 'translateX(-100%)';
    next.style.opacity = '1';

    requestAnimationFrame(function () { requestAnimationFrame(function () {
      current.style.transition = 'transform 0.45s cubic-bezier(0.4,0,0.2,1), opacity 0.45s ease';
      current.style.transform = fromRight ? 'translateX(-100%)' : 'translateX(100%)';
      current.style.opacity = '0.3';
      next.style.transition = 'transform 0.45s cubic-bezier(0.4,0,0.2,1), opacity 0.45s ease';
      next.style.transform = 'translateX(0)';
      next.style.opacity = '1';
    }); });

    setTimeout(function () {
      current.style.display = 'none';
      current.style.transition = '';
      current.style.transform = '';
      current.style.opacity = '';
      next.style.position = '';
      next.style.top = '';
      next.style.left = '';
      next.style.width = '';
      next.style.transition = '';
      next.style.transform = '';
      infoAnimating = false;
    }, 460);

    currentInfoTab = index;
    window.currentInfoTab = index;
    for (var i = 0; i < infoTabCount(); i++) {
      var tab = document.getElementById('info-tab-' + i);
      if (tab) {
        tab.style.background = i === index ? 'var(--accent)' : 'transparent';
        tab.style.color = i === index ? '#000' : 'var(--text-muted)';
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
      }
    }
  }

  function stepInfoTab(step) {
    var total = infoTabCount() || 1;
    setInfoTab((currentInfoTab + step + total) % total, step > 0 ? 'next' : 'prev');
  }

  /* ── FAQ Accordion ── */
  function initFAQ() {
    document.querySelectorAll('.faq-q').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('.faq-item');
        var body = item.querySelector('.faq-body');
        var isOpen = item.classList.contains('open');
        document.querySelectorAll('.faq-item.open').forEach(function (o) {
          o.classList.remove('open');
          o.querySelector('.faq-body').style.maxHeight = null;
          var q = o.querySelector('.faq-q');
          if (q) q.setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          item.classList.add('open');
          body.style.maxHeight = body.scrollHeight + 'px';
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
    // An open answer changes height when its text switches language.
    document.addEventListener('lang:change', function () {
      document.querySelectorAll('.faq-item.open .faq-body').forEach(function (body) {
        body.style.maxHeight = body.scrollHeight + 'px';
      });
    });
  }

  /* ── Mobile Menu ── */
  // Defined up front: the overlay's inline onclick handlers call it even
  // when the open button is missing (e.g. a section rendered on its own).
  window.closeMenu = function () {
    var overlay = document.querySelector('.nav-overlay');
    if (overlay) overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  function initMobileMenu() {
    var openBtn = document.querySelector('[data-menu-open]');
    var closeBtn = document.querySelector('[data-menu-close]');
    var overlay = document.querySelector('.nav-overlay');
    if (!openBtn || !overlay) return;
    openBtn.addEventListener('click', function () {
      overlay.classList.add('is-open');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    });
    function close() {
      overlay.classList.remove('is-open');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      openBtn.focus();
    }
    if (closeBtn) closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });
    window.closeMenu = close;
  }

  /* ── Quantity stepper ── */
  function initQuantity() {
    document.querySelectorAll('.qty:not([data-cart-qty]):not([data-cart-line])').forEach(function (wrap) {
      var input = wrap.querySelector('.qty__input');
      if (!input) return;

      function clamp() {
        var min = parseInt(input.min, 10) || 1;
        var value = parseInt(input.value, 10);
        // Typing letters, or emptying the box, must not leave it blank —
        // /cart/add.js would reject the line and the button would just fail.
        if (isNaN(value) || value < min) value = min;
        input.value = value;
        wrap.querySelectorAll('[data-qty-step]').forEach(function (btn) {
          if (parseInt(btn.dataset.qtyStep, 10) < 0) btn.disabled = value <= min;
        });
      }

      wrap.querySelectorAll('[data-qty-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          input.value = (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qtyStep, 10);
          clamp();
        });
      });
      input.addEventListener('change', clamp);
      input.addEventListener('blur', clamp);
      clamp();
    });
  }

  /* ── Tier card opens its product ──
     The whole card is clickable, but the buy controls inside it must keep
     working, so a click that lands on a link, button or the stepper is left
     alone. The tier name is a real link as well, which is what makes this
     reachable by keyboard. */
  function initCardLinks() {
    document.querySelectorAll('.ticket-card[data-product-url]').forEach(function (card) {
      card.classList.add('ticket-card--clickable');
      card.addEventListener('click', function (e) {
        if (e.target.closest('a, button, input, select, textarea, .qty')) return;
        // Let people select the text on a card without being navigated away.
        if (window.getSelection && String(window.getSelection()).length) return;
        window.location.href = card.dataset.productUrl;
      });
    });
  }

  /* ── Tier-card quantity, backed by the cart ──
     These steppers do not choose a quantity to add; they show what is already
     in the cart and change it. Hidden until the ticket is in there, and once
     the count reaches zero the line is removed and the button comes back. */
  function initCartSteppers() {
    var steppers = Array.prototype.slice.call(document.querySelectorAll('[data-cart-qty]'));
    if (!steppers.length) return;

    function paint(cart) {
      var byVariant = {};
      (cart.items || []).forEach(function (item) { byVariant[item.variant_id] = item; });

      steppers.forEach(function (wrap) {
        var card = wrap.closest('.ticket-card');
        var btn = card && card.querySelector('[data-add-to-cart]');
        var item = byVariant[Number(wrap.dataset.cartQty)];
        var input = wrap.querySelector('.qty__input');

        if (item) {
          input.value = item.quantity;
          wrap.dataset.lineKey = item.key;
          wrap.hidden = false;
          if (btn) btn.hidden = true;
        } else {
          delete wrap.dataset.lineKey;
          wrap.hidden = true;
          if (btn) { btn.hidden = false; restore(btn); }
        }
        wrap.querySelectorAll('[data-qty-step]').forEach(function (b) { b.disabled = false; });
      });
    }

    function change(wrap, quantity) {
      var key = wrap.dataset.lineKey;
      if (!key) return;
      wrap.querySelectorAll('[data-qty-step]').forEach(function (b) { b.disabled = true; });
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) { paint(cart); paintCartCount(cart); })
        // Leaving the buttons dead after a failed request would look like the
        // page had frozen, so re-read the cart and repaint from the truth.
        .catch(function () { refresh(); });
    }

    steppers.forEach(function (wrap) {
      wrap.querySelectorAll('[data-qty-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var input = wrap.querySelector('.qty__input');
          change(wrap, (parseInt(input.value, 10) || 0) + parseInt(btn.dataset.qtyStep, 10));
        });
      });
    });

    function refresh() {
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(paint)
        .catch(function () { /* offline: leave the buttons as the server drew them */ });
    }

    // A first-time visitor has an empty basket, and the server has already
    // said so in the header. There is no stepper state to restore, so the
    // round trip would only confirm nothing -- and this runs on every page
    // with ticket cards on it.
    var counted = document.querySelector('[data-cart-count]');
    if (!counted || (parseInt(counted.textContent, 10) || 0) > 0) refresh();
    // Handed a basket that has already been fetched, so the drawer can keep
    // the tier cards in step without a second trip for the same data.
    window.paintCartSteppers = paint;

    // Called after an add so the stepper appears without a page reload.
    window.refreshCartSteppers = refresh;
  }

  /* ── AJAX Add to Cart ── */
  function initAjaxCart() {
    // Guard the form itself, not just the button. A product form can be
    // submitted without the button ever being clicked — pressing Enter in the
    // quantity field does it — and a native POST leaves the browser sitting on
    // a POST result, so Back asks to resubmit the form and saying yes would buy
    // a second ticket.
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      if (form.dataset.cartBound) return;
      form.dataset.cartBound = '1';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('[data-add-to-cart]');
        if (btn && !btn.disabled) addToCart(btn);
      });
    });

    document.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
      // The theme editor re-runs this on every section render; without the flag
      // buttons elsewhere on the page collect a second listener and add twice.
      if (btn.dataset.cartBound) return;
      btn.dataset.cartBound = '1';
      btn.addEventListener('click', function (e) {
        // Cancel first. A tier card has no form, but the product page button is
        // a submit button, and bailing out later let the native POST through.
        e.preventDefault();
        addToCart(btn);
      });
    });
  }

  function addToCart(btn) {
    // The button carries the variant. The hidden field is the fallback for
    // the theme editor's placeholder product, which renders the button
    // without one.
    var idField = btn.form && btn.form.querySelector('[name="id"]');
    var variantId = btn.dataset.variantId || (idField && idField.value);
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = t('state-adding');

    // Record the tier and date on the line item itself. The wallet pass is
    // built from these, so the ticket says the right thing no matter how the
    // products are set up in Shopify. Keys starting with "_" are hidden from
    // the customer in the cart and at checkout.
    var properties = {};
    if (btn.dataset.tierFr) {
      properties[t('cart-tier-label')] = currentLang === 'en' && btn.dataset.tierEn
        ? btn.dataset.tierEn : btn.dataset.tierFr;
      properties._tier_fr = btn.dataset.tierFr;
      properties._tier_en = btn.dataset.tierEn || btn.dataset.tierFr;
    }
    if (btn.dataset.eventFr) {
      properties._event_fr = btn.dataset.eventFr;
      properties._event_en = btn.dataset.eventEn || btn.dataset.eventFr;
    }

    // On the product page the stepper sits inside the product form; on a
    // tier card there is no form at all, so the card's own wrapper is the
    // scope. Anything with neither falls back to one.
    var scope = btn.form || btn.closest('.ticket-card');
    var qtyField = scope && scope.querySelector('[name="quantity"]');
    var quantity = Math.max(1, parseInt(qtyField && qtyField.value, 10) || 1);

    /* No drawer is opened here any more. The reference this store is modelled
       on does the plainest possible thing -- the button says it is adding, and
       the page lands on the cart -- and beside that, a panel flying in with a
       skeleton preview in it read as a toy. */

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity, properties: properties })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        // The page is leaving, so the button is left saying what it is doing
        // and nothing is repainted: any counter or stepper update here would
        // be drawn for a frame and then thrown away with the document.
        window.location.href = '/cart';
      })
      .catch(function () {
        // Nothing was added, so leaving an open drawer promising otherwise
        // would be worse than not having opened it.
        if (window.closeCartDrawer) window.closeCartDrawer();
        btn.textContent = t('state-retry');
        setTimeout(function () { restore(btn); }, 2000);
      });
  }

  // Re-reads the button's own translation key so the label comes back
  // in whatever language is active when the timer fires.
  function restore(btn) {
    var key = btn.getAttribute('data-i18n');
    btn.textContent = key ? t(key) : t('add-to-cart');
    btn.disabled = false;
  }

  // Adding a ticket used to cost four round trips: the POST, a /cart.js to
  // repaint the badge, then the drawer fetching /cart.js and the whole /cart
  // page, then a third /cart.js. On a phone that is most of the wait. Anything
  // that already holds a basket paints from it instead of asking again.
  function paintCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      var changed = el.textContent.trim() !== String(cart.item_count);
      el.textContent = cart.item_count;
      /* An empty cart showing a "0" is a label announcing that nothing has
         happened. The count appears when there is something to count and is
         out of the layout entirely until then. */
      el.hidden = cart.item_count === 0;
      // The count used to scale up and the pill flash whenever this changed.
      // Both are gone: the number changing is the message, and a 1.28x pop on
      // a header badge is the loudest thing on an otherwise still page.
      void changed;
    });
  }

  // Only for the paths that have no basket to hand: coming back from bfcache,
  // or returning to a tab that may have moved on elsewhere.
  function updateCartCount() {
    fetch('/cart.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(paintCartCount)
      .catch(function () { /* cart unavailable in the theme editor preview */ });
  }

  /* ── Keep the page honest after a back/forward ──
     bfcache restores a page from memory rather than re-running it, so a cart
     that has changed since -- in the drawer, in another tab, at checkout --
     comes back on screen exactly as it was. The count can be repainted in
     place; the cart page itself is rendered from the basket by Liquid, so
     nothing short of a reload will do. */
  function initCartFreshness() {
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      if (window.location.pathname.indexOf('/cart') === 0) { window.location.reload(); return; }
      updateCartCount();
    });
    // Coming back to the tab counts too: the basket may have moved on in
    // another one.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') updateCartCount();
    });
  }

  /* ── Cart drawer ──
     Adding a ticket used to change a button label and a number in the header
     and nothing else, which left people with no idea where the basket was.
     The drawer opens on every successful add and on the header cart button.

     It is painted from /cart.js each time it opens rather than rendered with
     the page, so it cannot show a stale basket. Prices arrive in cents. */
  function initCartDrawer() {
    var root = document.querySelector('[data-cart-drawer]');
    if (!root) return;
    var panel = root.querySelector('.cart-drawer__panel');
    var body = root.querySelector('[data-cart-drawer-body]');
    var foot = root.querySelector('[data-cart-drawer-foot]');
    var totalEl = root.querySelector('[data-cart-drawer-total]');
    var lastFocus = null;

    function money(cents) {
      return '€' + (cents / 100).toFixed(2).replace('.', ',').replace(',00', '');
    }

    function esc(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    // Line properties carry the tier and the date. Keys starting with "_" are
    // the machine-readable copies the ticket is built from, not for reading.
    function metaOf(item) {
      var bits = [];
      var props = item.properties || {};
      Object.keys(props).forEach(function (k) {
        if (k.charAt(0) === '_' || !props[k]) return;
        bits.push(props[k]);
      });
      if (!bits.length && item.variant_title && item.variant_title !== 'Default Title') {
        bits.push(item.variant_title);
      }
      return bits.join(' // ');
    }

    // Add-on lines are skipped here because each one already has its own
    // control below, with its own price and its own way of being removed.
    // Listing them twice would put two switches on one thing, one of which
    // silently disagrees with the other.
    function render(cart, addonIds) {
      var items = cart.items.filter(function (it) {
        return !addonIds[String(it.variant_id)];
      });
      if (!items.length) {
        body.innerHTML = '<p class="cart-drawer__empty">' + esc(t('drawer-empty')) + '</p>';
        foot.hidden = true;
        // Hidden is not the same as correct. Left at its last value, the total
        // would flash the old figure for a frame the next time the footer is
        // shown with something in the basket.
        totalEl.textContent = money(0);
        return;
      }
      foot.hidden = false;
      // The total is the basket's own, so it still counts the add-ons even
      // though their lines are not drawn here.
      totalEl.textContent = money(cart.total_price);
      body.innerHTML = items.map(function (item) {
        // Line numbers the Cart API understands are positions in the whole
        // basket, not in this filtered list.
        var line = cart.items.indexOf(item) + 1;
        var meta = metaOf(item);
        var img = item.image
          ? '<img class="cart-drawer__thumb" src="' + esc(item.image) + '" alt="" width="64" height="64" loading="lazy">'
          : '<span class="cart-drawer__thumb"></span>';
        return '<div class="cart-drawer__item">' + img +
          '<div>' +
            '<p class="cart-drawer__name">' + esc(item.product_title) + '</p>' +
            (meta ? '<p class="cart-drawer__meta">' + esc(meta) + '</p>' : '') +
            '<div class="cart-drawer__row">' +
              '<span class="cart-drawer__qty">' +
                '<button type="button" data-line="' + line + '" data-qty="' + (item.quantity - 1) + '" aria-label="' + esc(t('drawer-qty-down')) + '">−</button>' +
                '<span>' + item.quantity + '</span>' +
                '<button type="button" data-line="' + line + '" data-qty="' + (item.quantity + 1) + '" aria-label="' + esc(t('drawer-qty-up')) + '">+</button>' +
              '</span>' +
              '<span class="cart-drawer__price">' + money(item.final_line_price) + '</span>' +
            '</div>' +
            '<button type="button" class="cart-drawer__remove" data-line="' + line + '" data-qty="0">' + esc(t('drawer-remove')) + '</button>' +
          '</div>' +
        '</div>';
      }).join('');
    }

    var addons = root.querySelector('[data-cart-drawer-addons]');

    // The add-on controls are Liquid: per-ticket quantities, and the rule that
    // hides merch when an exclusive pack is in the basket. Rather than port any
    // of that to JavaScript, the drawer fetches the cart page and lifts the
    // rendered nodes out of it. Shopify's section rendering API would be the
    // tidier route, but it only answers to a template-specific section id that
    // is not knowable from the homepage, where this drawer mostly opens.
    // True while a stranded add-on is being taken out, so the reload it
    // triggers cannot start a second removal on top of the first.
    var reconciling = false;

    function renderAddons(doc) {
      if (!addons) return;
      var nodes = doc.querySelectorAll('[data-cart-addon]');
      addons.innerHTML = '';
      var shown = 0;
      var stranded = null;   // { variantId: wantedQuantity }

      Array.prototype.forEach.call(nodes, function (node) {
        var have = parseInt(node.getAttribute('data-have'), 10) || 0;
        var need = parseInt(node.getAttribute('data-need'), 10) || 0;
        var line = parseInt(node.getAttribute('data-line'), 10) || 0;

        // An add-on whose quantity no longer matches the basket. Removing the
        // last ticket while insurance and parking were ticked used to leave
        // both of them sitting in the cart with nothing to attach to: the
        // header counted them and checkout would have charged for them, while
        // this panel -- which hides add-on lines, because each one has its own
        // control -- had nothing left to draw and said the cart was empty.
        //
        // The cart page has always fixed this; it renders a hidden marker and
        // its own script clears the line. The drawer was skipping those
        // markers as having nothing to show, which was true, and missing that
        // they are also an instruction.
        if (have > 0 && have !== need) {
          stranded = stranded || {};
          stranded[node.getAttribute('data-variant')] = need;
        }

        if (node.hasAttribute('hidden')) return;
        // .reveal starts at opacity 0 and waits for a scroll observer that will
        // never see a node inside a drawer. Strip it or the add-ons are present
        // and invisible.
        node.classList.remove('reveal');
        addons.appendChild(node);
        shown++;
      });

      if (stranded && !reconciling) {
        reconciling = true;
        root.classList.add('is-busy');
        // /cart/update.js, keyed by variant, rather than /cart/change.js by
        // line. Removing the last ticket can strand more than one add-on at
        // once -- insurance and parking together -- and every removal by line
        // shifts the index of everything after it, so a batch of them cannot
        // be done by position. Quantities by variant have no such ordering.
        post('/cart/update.js', { updates: stranded })
          .then(function () { return load(); })
          .catch(function () {})
          .then(function () {
            reconciling = false;
            root.classList.remove('is-busy');
          });
        return;
      }

      addons.hidden = shown === 0;
      bindAddons();
    }

    function bindAddons() {
      addons.querySelectorAll('.cart-insurance__input').forEach(function (box) {
        box.addEventListener('change', function () {
          var card = box.closest('[data-cart-addon]');
          card.classList.add('is-busy');
          // The control cannot be used again until the basket has come back
          // and this node has been replaced by a freshly rendered one.
          box.disabled = true;

          // Keyed by variant, not by line. A line number is a position in the
          // basket and every add or remove renumbers everything after it, so a
          // second click landing before the panel repainted would act on a
          // number that now pointed at something else -- which is how
          // unticking insurance could take the ticket with it.
          //
          // A quantity for a variant is also idempotent: setting it twice is
          // the same as setting it once, so an impatient double click is
          // harmless rather than destructive.
          var updates = {};
          updates[card.getAttribute('data-variant')] =
            box.checked ? (parseInt(card.getAttribute('data-need'), 10) || 1) : 0;

          post('/cart/update.js', { updates: updates })
            .then(function () { return load(); })
            .catch(function () {
              card.classList.remove('is-busy');
              box.disabled = false;
              box.checked = !box.checked;
            });
        });
      });
    }

    function post(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      });
    }

    // Which variants are add-ons rather than tickets. Learned from the cart
    // page and kept for the session: it only changes when the shop's add-on
    // products change, and knowing it up front is what lets the panel draw
    // from /cart.js alone instead of waiting on a whole HTML document.
    var addonIds = (function () {
      try { return JSON.parse(sessionStorage.getItem('addonVariants') || '{}'); }
      catch (e) { return {}; }
    })();

    function load() {
      var basket = fetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
        .then(function (r) { return r.json(); });
      var page = fetch('/cart', { headers: { 'Accept': 'text/html' }, cache: 'no-store' })
        .then(function (r) { return r.text(); });

      // The lines and the total come from the basket, which is a couple of
      // kilobytes, and are drawn the moment it arrives. The add-on controls
      // need the cart page's Liquid -- per-ticket quantities, the exclusive
      // pack rule -- so they follow when it lands, without holding up the
      // panel. The header badge is painted from the same basket rather than
      // fetched again.
      var drawn = basket.then(function (cart) {
        paintCartCount(cart);
        if (window.paintCartSteppers) window.paintCartSteppers(cart);
        render(cart, addonIds);
        return cart;
      });

      return Promise.all([drawn, page])
        .then(function (r) {
          var doc = new DOMParser().parseFromString(r[1], 'text/html');
          var seen = {};
          doc.querySelectorAll('[data-cart-addon]').forEach(function (n) {
            seen[n.getAttribute('data-variant')] = true;
          });
          // A first-ever open has nothing cached, so the lines were drawn
          // without knowing which of them are add-ons. Redraw only if that
          // turned out to matter.
          var changed = Object.keys(seen).some(function (k) { return !addonIds[k]; });
          addonIds = seen;
          try { sessionStorage.setItem('addonVariants', JSON.stringify(seen)); } catch (e) {}
          if (changed) render(r[0], addonIds);
          renderAddons(doc);
        })
        .catch(function () { /* cart unavailable in the editor preview */ });
    }

    function change(line, quantity) {
      root.classList.add('is-busy');
      post('/cart/change.js', { line: line, quantity: quantity })
        .then(function () { return load(); })
        .then(function () {
          if (window.refreshCartSteppers) window.refreshCartSteppers();
        })
        .catch(function () {})
        .then(function () { root.classList.remove('is-busy'); });
    }

    body.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-line]');
      if (!btn) return;
      var qty = parseInt(btn.getAttribute('data-qty'), 10);
      if (isNaN(qty) || qty < 0) return;
      change(parseInt(btn.getAttribute('data-line'), 10), qty);
    });

    // Drawn while the basket is still being written to. Shopify's /cart/add.js
    // takes about half a second on a good connection and longer on a phone,
    // and waiting for it before opening meant half a second of a tapped button
    // and nothing else happening -- which reads as a site that did not
    // register the tap.
    function showPending(preview) {
      // Everything here is already on the page -- the tier name, the date and
      // the price are printed on the card that was just tapped -- so the line
      // can be drawn before the server has been told about it. It is replaced
      // by the real one the moment the basket comes back; this only fills the
      // half second Shopify's add-to-cart takes.
      var line = preview && preview.name
        ? '<p class="cart-drawer__name">' + esc(preview.name) + '</p>' +
          (preview.meta ? '<p class="cart-drawer__meta">' + esc(preview.meta) + '</p>' : '') +
          '<div class="cart-drawer__row">' +
            '<span class="cart-drawer__qty"><span>' + (preview.quantity || 1) + '</span></span>' +
            (preview.price ? '<span class="cart-drawer__price">' + esc(preview.price) + '</span>' : '') +
          '</div>'
        : '<span class="cart-drawer__skeleton" style="width:72%"></span>' +
          '<span class="cart-drawer__skeleton" style="width:38%"></span>';

      body.innerHTML =
        '<div class="cart-drawer__item cart-drawer__item--pending">' +
          '<span class="cart-drawer__thumb"></span><div>' + line + '</div>' +
        '</div>';
      foot.hidden = true;
      if (addons) { addons.hidden = true; addons.innerHTML = ''; }
    }

    function openDrawer(pending, preview) {
      lastFocus = document.activeElement;
      root.hidden = false;
      if (pending) showPending(preview); else load();
      // The panel has to be laid out at translate:100% before the class that
      // moves it to 0 lands, or there is nothing to animate between. Reading
      // offsetWidth forces that layout synchronously. requestAnimationFrame
      // would read better but does not fire at all while the tab is in the
      // background, which left the drawer un-hidden and un-slid -- open, and
      // invisible, parked off the right edge.
      void panel.offsetWidth;
      root.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      panel.focus();
    }

    function closeDrawer() {
      root.classList.remove('is-open');
      document.body.style.overflow = '';
      var done = function () { root.hidden = true; };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else setTimeout(done, 380);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    root.querySelectorAll('[data-cart-drawer-close]').forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });

    document.addEventListener('keydown', function (e) {
      if (root.hidden) return;
      if (e.key === 'Escape') { closeDrawer(); return; }
      if (e.key !== 'Tab') return;
      // Keep tabbing inside the panel while it is modal.
      var f = panel.querySelectorAll('a[href], button:not([disabled])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    // The header cart opens the drawer instead of navigating. Modified and
    // middle clicks still go to /cart, because that is what someone doing it
    // is asking for.
    document.querySelectorAll('a.cart-btn').forEach(function (a) {
      a.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        openDrawer(false);
      });
    });

    window.openCartDrawer = function (opts) {
      openDrawer(opts && opts.pending, opts && opts.preview);
    };
    window.refreshCartDrawer = load;
    window.closeCartDrawer = closeDrawer;
  }

  /* ── Cart lines: quantity and delete ──
     The cart page is rendered by Liquid, so every path here finishes with a
     reload rather than patching the line price, the summary rows, the total
     and the header count by hand and hoping the four agree. What it does do
     first is move the number the visitor just pressed, and dim the line: a
     stepper that sits still for the length of a round trip reads as broken,
     and that is the complaint this is meant to answer, not cause.

     Keyed by `item.key`, never by line index. Every removal renumbers the
     lines after it, so a click that lands before the page has come back would
     otherwise act on whatever has since moved into that position. */
  function initCartLines() {
    var wraps = Array.prototype.slice.call(document.querySelectorAll('[data-cart-line]'));
    var trash = Array.prototype.slice.call(document.querySelectorAll('[data-cart-remove]'));
    if (!wraps.length && !trash.length) return;

    function change(key, quantity, row) {
      if (row) row.classList.add('is-busy');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
      })
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          window.location.reload();
        })
        .catch(function () {
          // Put the line back rather than leaving it greyed and inert; a
          // reload would also lose whatever else is half-done on the page.
          if (row) row.classList.remove('is-busy');
        });
    }

    wraps.forEach(function (wrap) {
      var input = wrap.querySelector('.qty__input');
      var row = wrap.closest('.cart-item');
      wrap.querySelectorAll('[data-qty-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var next = (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qtyStep, 10);
          if (next < 1) return;                 // the delete button is the way to zero
          input.value = next;
          change(wrap.getAttribute('data-cart-line'), next, row);
        });
      });
      // Typed straight into the box. Anything unreadable falls back to what
      // the server last said this line held, which is still on the element.
      input.addEventListener('change', function () {
        var next = parseInt(input.value, 10);
        if (isNaN(next) || next < 1) next = 1;
        input.value = next;
        change(wrap.getAttribute('data-cart-line'), next, row);
      });
    });

    trash.forEach(function (btn) {
      btn.addEventListener('click', function () {
        change(btn.getAttribute('data-cart-remove'), 0, btn.closest('.cart-item'));
      });
    });
  }

  /* ── "Add to your order" cards ──
     A skin over the add-on checkboxes, which stay in the page (hidden by CSS)
     because they carry the per-ticket quantity, the exclusive-pack rule and
     the orphan cleanup, and because the cart drawer lifts those same nodes out
     of this page to build its own list. Pressing a card ticks the box and
     lets that tested path run, so there is one mechanism and two faces on it
     rather than two mechanisms that can disagree. */
  function initCartUpsell() {
    var cards = document.querySelectorAll('[data-upsell-toggle]');
    if (!cards.length) return;

    cards.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variant = btn.getAttribute('data-upsell-toggle');
        var node = document.querySelector('[data-cart-addon][data-variant="' + variant + '"]');
        var box = node && node.querySelector('.cart-insurance__input');
        if (!box) return;
        var card = btn.closest('.cart-upsell__card');
        if (card) card.classList.add('is-busy');
        box.checked = btn.getAttribute('data-upsell-on') === '1';
        box.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ── Cart add-ons ──
     Insurance, parking, merch: each is a checkbox bound to one product. An
     add-on marked "one per ticket" has to keep its line quantity equal to the
     number of tickets rather than sit at 1, so the quantity is read off the
     markup rather than assumed.

     Every path finishes with a reload: the cart page is rendered by Liquid, so
     the totals, the summary and the count in the header all come back correct
     without this having to patch any of them by hand. That also means line
     indexes are always freshly computed and never go stale. */
  function initCartAddons() {
    var nodes = document.querySelectorAll('[data-cart-addon]');
    if (!nodes.length) return;

    function post(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      });
    }

    function read(el, name) { return parseInt(el.getAttribute(name), 10) || 0; }

    // Someone changed the ticket count while an add-on was attached, so it no
    // longer matches the basket. Put the first mismatch right before they see
    // a wrong total; the reload brings us back here for any others.
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i], have = read(el, 'data-have'), need = read(el, 'data-need');
      var line = read(el, 'data-line');
      if (have > 0 && line && have !== need) {
        el.classList.add('is-busy');
        post('/cart/change.js', { line: line, quantity: need })
          .then(function () { window.location.reload(); })
          .catch(function () { el.classList.remove('is-busy'); });
        return;
      }
    }

    Array.prototype.forEach.call(nodes, function (root) {
      var box = root.querySelector('.cart-insurance__input');   // absent on an orphan node
      if (!box) return;
      box.addEventListener('change', function () {
        root.classList.add('is-busy');
        var work = box.checked
          ? post('/cart/add.js', {
              items: [{ id: Number(root.getAttribute('data-variant')), quantity: read(root, 'data-need') }]
            })
          : post('/cart/change.js', { line: read(root, 'data-line'), quantity: 0 });
        work.then(function () { window.location.reload(); })
            .catch(function () {
              root.classList.remove('is-busy');
              box.checked = !box.checked;   // put the control back where it was
            });
      });
    });
  }

  /* ── The hover lift, handed to the scroll on touch screens ──
     Desktop lights the card under the pointer. A phone has no pointer, so the
     card crossing a band across the middle of the screen is lit instead: the
     lift travels down the column as you scroll, one card at a time, the way it
     follows the mouse on a desktop.

     The band is deliberately thin. Lighting everything that is merely on
     screen would leave the whole column raised at once, which says nothing --
     the point of the state is that it singles one card out. */
  function initPassing() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var mq = window.matchMedia('(max-width: 768px)');
    var observer = null;

    function start() {
      if (observer) return;
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) entry.target.classList.add('is-passing');
          else entry.target.classList.remove('is-passing');
        });
      }, {
        // Top and bottom pulled in to 45%, leaving a 10% band across the
        // middle. A card is taller than the band, so it holds the state for
        // most of its pass and hands over cleanly to the next one.
        rootMargin: '-45% 0px -45% 0px',
        threshold: 0
      });
      document.querySelectorAll('.ticket-card, .bundle').forEach(function (el) {
        observer.observe(el);
      });
    }

    function stop() {
      if (!observer) return;
      observer.disconnect();
      observer = null;
      // Widening the window past the breakpoint hands the job back to the
      // mouse; anything left lit would stay lit with nothing to clear it.
      document.querySelectorAll('.is-passing').forEach(function (el) {
        el.classList.remove('is-passing');
      });
    }

    function sync() { if (mq.matches) start(); else stop(); }
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else if (mq.addListener) mq.addListener(sync);
  }

  /* ── Reveal on Scroll ── */
  function initReveal() {
    // Tells the watchdog in theme.liquid that the reveals have an owner. Set
    // first: every path out of this function leaves the page in a state that
    // is visible or will become visible, so none of them should be treated
    // as a failure.
    document.documentElement.setAttribute('data-reveal-ready', '');
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.reveal').forEach(function (el) { el.classList.add('visible'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('visible'); return; }
        // Not intersecting is not the same as gone. Re-arm only once the
        // element is clear of the viewport on one side or the other, so it
        // plays again next time you come back to it -- and never blinks off
        // while it is still on screen, which is what stripping the class on
        // any non-intersection would do at the edges.
        var box = entry.boundingClientRect;
        if (box.top >= window.innerHeight || box.bottom <= 0) {
          entry.target.classList.remove('visible');
        }
      });
    }, {
      // Nothing waits to be a tenth visible any more -- a tall block would
      // have to be well up the screen before it started. It goes as soon as
      // it clears the bottom edge, less a 60px lead so the movement begins
      // just before you would otherwise be reading it.
      threshold: 0,
      rootMargin: '0px 0px -60px 0px'
    });

    var items = document.querySelectorAll('.reveal');
    items.forEach(function (el) {
      // Stagger within the group the element belongs to. The old version
      // numbered every .reveal on the page and took the index mod 6, so a
      // card's delay depended on how many unrelated elements happened to
      // appear before it in the document -- the first card in a section
      // could easily start later than the second.
      // This is an animation delay, not a transition delay: see the note on
      // .reveal in theme.css for why the entrance cannot be a transition.
      var sibs = el.parentNode ? el.parentNode.children : [el];
      var n = 0;
      for (var i = 0; i < sibs.length && sibs[i] !== el; i++) {
        if (sibs[i].classList && sibs[i].classList.contains('reveal')) n++;
      }
      el.style.animationDelay = Math.min(n, 5) * 90 + 'ms';
      observer.observe(el);
    });

    // The 60px lead means an element sitting entirely inside that band when
    // the page is already scrolled as far as it goes would never be asked to
    // appear. At the bottom of the page, show whatever is left. This does not
    // fight the observer: an element in that band is on screen, so it is
    // neither of the two cases that re-arm one.
    window.addEventListener('scroll', function () {
      if (window.pageYOffset + window.innerHeight < document.documentElement.scrollHeight - 2) return;
      items.forEach(function (el) { el.classList.add('visible'); });
    }, { passive: true });
  }

  /* ── Header: no seam until the page moves ──
   *
   * The header and the hero are the same black, so the only thing drawing a
   * line across the top of an unscrolled page was the header's own bottom
   * border. Sitting there at rest it reads as a bar bolted above the design;
   * the reference site has no line at rest and gains one the moment you
   * scroll, which is when a line is actually doing something -- separating
   * fixed furniture from content moving underneath it.
   *
   * Threshold is 8px rather than 0 so a rubber-band overscroll or a browser
   * restoring scroll position by a pixel does not flicker the border.
   */
  (function () {
    /* theme.js is emitted by `script_tag` into the head, and -- contrary to
       what the Orelsan log recorded -- the tag it produces here carries
       neither defer nor async. It therefore executes while the head is still
       parsing, before <body> exists, so querying for the header at this point
       returns null and the whole feature silently does nothing. Everything
       else in this file survives that because it either re-queries later or
       runs off an event; this needs the guard. */
    function start() {
      var header = document.querySelector('.header');
      if (!header) return;
      /* The sticky buy bar rides the same scroll listener. While the hero's
         own button is still on screen the bar would be the same call to
         action twice, with one of them covering content, so it waits until
         the hero has gone by -- measured against the hero's real height
         rather than a guessed pixel value, so it still lands correctly when
         the hero is a different size on a phone.

         `aria-hidden` and `tabindex` move with the visibility: neither the
         keyboard nor a screen reader should reach a button that nobody can
         see. */
      /* The hero is pulled up underneath the header so the backdrop runs to
         the very top of the viewport, which is the only way a transparent
         header shows anything but the page colour. That pull has to equal the
         header's real height -- it differs between desktop and mobile, and
         changes again if the wordmark wraps -- so it is measured and published
         as a custom property rather than guessed at in the stylesheet. */
      var lastH = null;
      function publishHeaderHeight() {
        var h = header.offsetHeight;
        if (h === lastH) return;   // writing the same value still costs a recalc
        lastH = h;
        document.documentElement.style.setProperty('--header-h', h + 'px');
      }
      publishHeaderHeight();
      /* A ResizeObserver on the header was tried here and hung the page. The
         property it writes drives the hero's margin and padding, that relayout
         is observed, and `offsetHeight` rounding makes the measurement
         oscillate by a fraction of a pixel -- so it never settles and the main
         thread never comes back. `resize` is the right signal anyway: the
         header's height only changes when the viewport crosses a breakpoint.
         The equality guard above is belt and braces. */
      window.addEventListener('resize', publishHeaderHeight, { passive: true });

      var bar    = document.getElementById('sticky-bar');
      var barCta = bar && bar.querySelector('.sticky-bar__cta');
      var hero   = document.querySelector('.hero-pb');

      var stuck = null, barOn = null;
      function sync() {
        var y = window.pageYOffset;

        var nextStuck = y > 8;
        if (nextStuck !== stuck) {          // only touch the DOM on a change
          stuck = nextStuck;
          header.classList.toggle('is-stuck', nextStuck);
        }

        if (bar) {
          var nextBar = hero ? y > hero.offsetHeight - 120 : nextStuck;
          if (nextBar !== barOn) {
            barOn = nextBar;
            bar.classList.toggle('is-on', nextBar);
            bar.setAttribute('aria-hidden', nextBar ? 'false' : 'true');
            if (barCta) barCta.setAttribute('tabindex', nextBar ? '0' : '-1');
          }
        }
      }
      sync();
      window.addEventListener('scroll', sync, { passive: true });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  })();

  /* ── Search overlay and country picker ── */
  (function () {
    function start() {
      /* --- Search --- */
      var overlay = document.getElementById('search-overlay');
      var input   = overlay && overlay.querySelector('[data-search-input]');
      var opener  = document.querySelector('[data-search-open]');
      var lastFocus = null;

      function openSearch() {
        if (!overlay) return;
        lastFocus = document.activeElement;
        overlay.hidden = false;
        if (opener) opener.setAttribute('aria-expanded', 'true');
        /* The field is the only reason the overlay exists, so it takes focus --
           but not in the same tick, because an element unhidden a moment ago is
           not yet focusable. `setTimeout` rather than `requestAnimationFrame`:
           rAF does not fire at all in a backgrounded tab, so a search opened by
           a restored session would come back with no focus and no explanation. */
        setTimeout(function () { if (input) input.focus(); }, 0);
        document.addEventListener('keydown', onKey);
      }
      function closeSearch() {
        if (!overlay) return;
        overlay.hidden = true;
        if (opener) opener.setAttribute('aria-expanded', 'false');
        document.removeEventListener('keydown', onKey);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }
      function onKey(e) { if (e.key === 'Escape') closeSearch(); }

      if (opener) opener.addEventListener('click', openSearch);
      document.querySelectorAll('[data-search-close]').forEach(function (b) {
        b.addEventListener('click', closeSearch);
      });
      /* Clicking the backdrop closes; clicking the form inside must not. */
      if (overlay) overlay.addEventListener('click', function (e) {
        if (e.target === overlay) closeSearch();
      });

      /* --- Country / currency ---
         Options arrive as JSON and become elements on first use. Doing it in
         markup cost 86KB -- over half the document -- on every single page
         view, for a control most visitors never touch. */
      var cData = null, cBuilt = false;
      function countryData() {
        if (cData) return cData;
        var el = document.querySelector('[data-country-data]');
        try { cData = el ? JSON.parse(el.textContent) : []; } catch (e) { cData = []; }
        return cData;
      }
      function buildCountryList() {
        if (cBuilt) return;
        var ul = document.querySelector('[data-country-list]');
        if (!ul) return;
        var html = '';
        countryData().forEach(function (c) {
          html += '<li><button type="submit" name="country_code" value="' + c.c +
                  '" class="country-option' + (c.s ? ' is-current' : '') + '">' +
                  '<span></span><span class="country-option__code"></span></button></li>';
        });
        ul.innerHTML = html;
        /* Names go in as text, never as HTML: they come from Shopify but they
           are still data being turned into a document, and building markup by
           concatenation is how that becomes an injection. */
        var items = ul.querySelectorAll('.country-option');
        countryData().forEach(function (c, i) {
          if (!items[i]) return;
          items[i].children[0].textContent = c.n;
          items[i].children[1].textContent = c.u;
        });
        cBuilt = true;
      }

      /* The overlay's native picker, from the same data. */
      (function () {
        var sel = document.querySelector('[data-country-select]');
        if (!sel) return;
        function fill() {
          if (sel.options.length) return;
          countryData().forEach(function (c) {
            var o = document.createElement('option');
            o.value = c.c;
            o.textContent = c.n + ' — ' + c.u;
            if (c.s) o.selected = true;
            sel.appendChild(o);
          });
        }
        /* Filled when the menu opens, or on first interaction with the select
           itself -- whichever happens first. */
        var opener = document.querySelector('[data-menu-open]');
        if (opener) opener.addEventListener('click', fill);
        sel.addEventListener('focus', fill);
        sel.addEventListener('mousedown', fill);
      })();

      var cToggle = document.querySelector('[data-country-toggle]');
      var cPanel  = document.getElementById('country-list');
      var cSearch = document.querySelector('[data-country-search]');
      if (cToggle && cPanel) {
        cToggle.addEventListener('click', function () {
          buildCountryList();
          var open = cPanel.hidden;
          cPanel.hidden = !open;
          cToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (open && cSearch) setTimeout(function () { cSearch.focus(); }, 0);
        });
        document.addEventListener('click', function (e) {
          if (cPanel.hidden) return;
          if (cPanel.contains(e.target) || cToggle.contains(e.target)) return;
          cPanel.hidden = true;
          cToggle.setAttribute('aria-expanded', 'false');
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && !cPanel.hidden) {
            cPanel.hidden = true;
            cToggle.setAttribute('aria-expanded', 'false');
            cToggle.focus();
          }
        });
      }
      /* Filtering a list of a hundred-odd countries is the difference between
         a usable control and a scroll hunt. */
      if (cSearch) cSearch.addEventListener('input', function () {
        var q = cSearch.value.trim().toLowerCase();
        cPanel.querySelectorAll('.country-list li').forEach(function (li) {
          li.hidden = q !== '' && li.textContent.toLowerCase().indexOf(q) === -1;
        });
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else { start(); }
  })();

  /* ── Init ── */
  /* This ran inline, and this file executes during head parsing, before
     <body> exists -- so on every page load `setLang` queried an empty document
     and did nothing. The language only ever applied when the switcher was
     clicked, which is why the page always arrived in whatever language Liquid
     had printed. It has to wait for the DOM.

     It matters more now: the store this theme previews against has French as
     its primary language, which is a Shopify setting rather than a theme one,
     so Liquid prints French no matter which locale file is marked default.
     Running the pass on DOM ready is what makes the page English here. On the
     real store, with English primary, Liquid prints English and this pass has
     nothing left to change. */
  function initLang() { setLang(storedLang()); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLang, { once: true });
  } else {
    initLang();
  }

  /* ------------------------------------------------------------------ *
   * Seating map                                                         *
   *                                                                     *
   * Hovering a block lights every block in the same zone and fills the   *
   * readout; clicking pins that zone. The prices and the link target     *
   * come from the active date tab, so one map serves all three nights    *
   * and can never quote a price the ticket cards below do not.           *
   * ------------------------------------------------------------------ */
  function initSeatmap() {
    document.querySelectorAll('[data-seatmap]').forEach(function (root) {
      if (root.dataset.seatmapReady === '1') return;
      root.dataset.seatmapReady = '1';

      var svg = root.querySelector('.seatmap');
      var blocks = Array.prototype.slice.call(root.querySelectorAll('.seatmap__block'));
      var legendBtns = Array.prototype.slice.call(root.querySelectorAll('[data-seatmap-legend-btn]'));
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-seatmap-tab]'));
      var nameEl = root.querySelector('[data-seatmap-name]');
      var noteEl = root.querySelector('[data-seatmap-note]');
      var priceEl = root.querySelector('[data-seatmap-price]');
      var cta = root.querySelector('[data-seatmap-cta]');
      if (!svg || !tabs.length || !legendBtns.length) return;

      var selected = legendBtns[0].getAttribute('data-zone');
      var activeTab = tabs[0];
      var soldRow = root.querySelector('[data-seatmap-soldout-row]');

      /* Which zone a block belongs to depends on the night. A block carries
         its usual zone in data-zone and, where a night differs, an override
         in data-zone-<date id>. `data-zone-now` is the resolved answer for the
         night on screen, and it is what the CSS paints from -- Liquid writes
         it for the first date so nothing flashes the wrong colour before this
         runs. */
      function dateId() { return activeTab.getAttribute('data-date-id') || ''; }

      function zoneOf(block) {
        return block.getAttribute('data-zone-' + dateId()) ||
               block.getAttribute('data-zone');
      }

      function applyZones() {
        blocks.forEach(function (b) { b.setAttribute('data-zone-now', zoneOf(b)); });
      }

      function zoneCount(zone) {
        var n = 0;
        blocks.forEach(function (b) { if (zoneOf(b) === zone) n++; });
        return n;
      }

      /** The legend button is the single source for a zone's two languages. */
      function zoneText(zone, part) {
        var btn = root.querySelector('[data-seatmap-legend-btn][data-zone="' + zone + '"]');
        if (!btn) return '';
        var el = btn.querySelector('.seatmap__legend-' + part);
        if (!el) return '';
        return el.textContent;
      }

      function priceOf(zone) {
        var raw = activeTab.getAttribute('data-price-' + zone);
        return raw ? '€' + raw : '—';
      }

      // Dimming the rest of the hall is an answer to a question, so it waits
      // until one has been asked. Until the first hover or click the plan sits
      // at full strength -- every block the same green, the way the printed
      // plan looks -- and the panel is still filled in from the default zone.
      var engaged = false;

      function light(zone) {
        blocks.forEach(function (b) {
          b.classList.toggle('is-lit', zoneOf(b) === zone);
        });
        svg.classList.toggle('is-focused', engaged && !!zone);
      }

      function engage() {
        if (engaged) return;
        engaged = true;
      }

      /** Fill the readout for `zone` without changing what is pinned. */
      function show(zone) {
        if (nameEl) nameEl.textContent = zoneText(zone, 'name');
        if (noteEl) noteEl.textContent = zoneText(zone, 'note');
        if (priceEl) priceEl.textContent = priceOf(zone);
        if (cta) {
          var tier = activeTab.getAttribute('data-tier-' + zone);
          var date = dateId();
          // A zone whose ticket has not been set up yet has no card to land
          // on, so it goes to the night's ticket section rather than to a
          // dead anchor.
          var card = tier && date ? document.getElementById(date + '-' + tier) : null;
          cta.setAttribute('href', card ? '#' + date + '-' + tier : '#tickets-' + date);
        }
        light(zone);
      }

      function select(zone) {
        selected = zone;
        legendBtns.forEach(function (b) {
          b.setAttribute('aria-pressed', b.getAttribute('data-zone') === zone ? 'true' : 'false');
        });
        show(zone);
      }

      /* The zone names are not fixed across the three nights -- the upper tier
         is "Categorie 2" on 9 DEC and "Categorie 1" on the other two -- so the
         map takes each name from the very ticket card that zone links to
         rather than from a setting of its own. Nothing to keep in sync: rename
         a tier on its product card and the plan follows.

         Both languages are copied across, so the FR/EN switch keeps working on
         a name that arrived after page load. If the ticket section is not on
         the page, the section's own setting stays put. */
      /* Nights do not all sell the same zones. A legend row whose zone has no
         blocks on this night, or no price set for it, is taken off the list
         rather than shown priced at an em dash. */
      function paintLegendVisibility() {
        var firstShown = null;
        legendBtns.forEach(function (btn) {
          var zone = btn.getAttribute('data-zone');
          var has = zoneCount(zone) > 0 && !!activeTab.getAttribute('data-price-' + zone);
          btn.parentNode.hidden = !has;
          if (has && !firstShown) firstShown = zone;
        });
        if (soldRow) soldRow.hidden = zoneCount('soldout') === 0;
        // If the night on screen does not sell what was selected, fall back to
        // the first zone it does sell.
        if (zoneCount(selected) === 0 || !activeTab.getAttribute('data-price-' + selected)) {
          selected = firstShown || selected;
          legendBtns.forEach(function (b) {
            b.setAttribute('aria-pressed', b.getAttribute('data-zone') === selected ? 'true' : 'false');
          });
        }
      }

      function paintLegendNames() {
        legendBtns.forEach(function (btn) {
          var zone = btn.getAttribute('data-zone');
          var tier = activeTab.getAttribute('data-tier-' + zone);
          var date = activeTab.getAttribute('data-date-id');
          var card = tier && date ? document.getElementById(date + '-' + tier) : null;
          var src = card ? card.querySelector('.ticket-tier-name') : null;
          var out = btn.querySelector('.seatmap__legend-name');
          if (!src || !out) return;
          out.textContent = src.textContent;
        });
      }

      function paintLegendPrices() {
        legendBtns.forEach(function (btn) {
          var out = btn.querySelector('[data-seatmap-legend-price]');
          if (out) out.textContent = priceOf(btn.getAttribute('data-zone'));
        });
      }

      function setTab(tab) {
        activeTab = tab;
        tabs.forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        applyZones();
        paintLegendVisibility();
        paintLegendNames();
        paintLegendPrices();
        show(selected);
      }

      blocks.forEach(function (block) {
        // Read the zone at event time, not now: it changes with the date.
        block.addEventListener('mouseenter', function () {
          var zone = zoneOf(block);
          if (zone === 'soldout') return;
          engage(); show(zone);
        });
        block.addEventListener('mouseleave', function () {
          if (zoneOf(block) === 'soldout') return;
          show(selected);
        });
        block.addEventListener('click', function () {
          var zone = zoneOf(block);
          if (zone === 'soldout') return;      // nothing left to sell here
          engage(); select(zone);
        });
      });

      legendBtns.forEach(function (btn) {
        var zone = btn.getAttribute('data-zone');
        btn.addEventListener('mouseenter', function () { engage(); show(zone); });
        btn.addEventListener('mouseleave', function () { show(selected); });
        btn.addEventListener('focus', function () { engage(); show(zone); });
        btn.addEventListener('blur', function () { show(selected); });
        btn.addEventListener('click', function () { engage(); select(zone); });
      });

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () { setTab(tab); });
      });

      // Land on the ticket card itself and flash it, so the jump is not a
      // wall of three identical cards with no clue which one was meant.
      if (cta) {
        cta.addEventListener('click', function (e) {
          var id = cta.getAttribute('href') || '';
          if (id.charAt(0) !== '#') return;
          var target = document.querySelector(id);
          if (!target) return;                       // let the browser try the anchor
          e.preventDefault();
          var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (window.themeScrollTo && !reduce) {
            var box = target.getBoundingClientRect();
            window.themeScrollTo(box.top + window.pageYOffset - (window.innerHeight - box.height) / 2);
          } else {
            target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
          }
          target.classList.remove('is-flash');
          void target.offsetWidth;                   // restart the animation
          target.classList.add('is-flash');
          window.setTimeout(function () { target.classList.remove('is-flash'); }, 1600);
        });
      }

      document.addEventListener('lang:change', function () {
        paintLegendPrices();
        show(selected);
      });

      setTab(tabs[0]);
      select(selected);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    setLang(currentLang);
    initFAQ();
    initPassing();
    initCartFreshness();
    initCartDrawer();
    initCartAddons();
    initCartLines();
    initCartUpsell();
    initMobileMenu();
    initAjaxCart();
    initQuantity();
    initCartSteppers();
    initCardLinks();
    initReveal();
    initSeatmap();
    // Not on first load: Liquid has already rendered the count from the same
    // basket this would go and ask about, so the request only ever confirms
    // what is on screen. The pageshow and visibilitychange handlers cover the
    // cases where it can actually have gone stale.
  });

  // Shopify theme editor: sections re-render without a page load.
  document.addEventListener('shopify:section:load', function () {
    setLang(currentLang);
    initFAQ();
    initAjaxCart();
    initReveal();
    initSeatmap();
  });

  // Exposed for inline onclick handlers in the sections.
  window.setLang = setLang;
  window.setInfoTab = setInfoTab;
  window.stepInfoTab = stepInfoTab;
  window.t = t;
  window.currentLang = currentLang;
  window.currentInfoTab = currentInfoTab;

})();

/* ============================================================
   MOTION LAYER
   The CSS in theme.css does the actual moving. This block only
   supplies the three things CSS cannot see for itself: where a
   click landed, when a countdown digit changed, and which arrow
   glyphs are trailing arrows worth animating.
   Kept in its own closure so it touches nothing above it.
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ── Trailing arrows ──
     Buttons and links spell their arrows inline ("BILLETS →"). Wrapping the
     glyph lets CSS glide it on hover without touching any of the copy or the
     translation dictionaries — the text content is unchanged either way. */
  var ARROW_END = /\s*([→⟶])\s*$/;      // → ⟶
  var ARROW_START = /^\s*([←⟵])\s*/;    // ← ⟵

  // A control whose whole label sits in one wrapper span (the date stepper
  // does this so the text and the aria-label can be translated separately)
  // keeps its arrow one level down, so follow a lone element child inward.
  function textHost(el) {
    var node = el;
    for (var i = 0; i < 3; i++) {
      var kids = node.childNodes;
      if (kids.length !== 1 || kids[0].nodeType !== 1) break;
      node = kids[0];
    }
    return node;
  }

  function wrapArrows(root) {
    var nodes = (root || document).querySelectorAll('a, button, .btn');
    Array.prototype.forEach.call(nodes, function (host) {
      if (host.dataset.arwBound) return;
      var el = textHost(host);
      // Only touch a plain text tail; anything with mixed element children of
      // its own is left alone so we never rearrange a section's markup.
      var last = el.lastChild;
      if (last && last.nodeType === 3 && ARROW_END.test(last.nodeValue)) {
        var m = last.nodeValue.match(ARROW_END);
        last.nodeValue = last.nodeValue.replace(ARROW_END, ' ');
        var span = document.createElement('span');
        span.className = 'arw';
        span.textContent = m[1];
        el.appendChild(span);
        host.dataset.arwBound = '1';
        return;
      }
      var first = el.firstChild;
      if (first && first.nodeType === 3 && ARROW_START.test(first.nodeValue)) {
        var m2 = first.nodeValue.match(ARROW_START);
        first.nodeValue = first.nodeValue.replace(ARROW_START, ' ');
        var span2 = document.createElement('span');
        span2.className = 'arw arw--back';
        span2.textContent = m2[1];
        el.insertBefore(span2, first);
        host.dataset.arwBound = '1';
      }
    });
  }

  /* ── Click ripple ──
     Positioned from the pointer, sized to cover the furthest corner so the
     wave always finishes past the edge rather than stopping mid-button. */
  var RIPPLE_TARGETS = '.btn, .info-step-btn, .footer-social, .seatmap__legend-btn,' +
                       '.cart-btn, .tour-link-btn, .faq-q, .seatmap__tab,' +
                       '#btn-fr, #btn-en, #mob-btn-fr, #mob-btn-en';

  /* Buying a ticket is the one press on this site that must not feel playful,
     so the wave is kept off the buy buttons specifically rather than removed
     everywhere -- a nav or a tab can afford it. */
  var NO_RIPPLE = '[data-add-to-cart], [data-dp-submit], [data-dp-vip], [data-tp-submit]';

  function ripple(e) {
    if (REDUCED.matches) return;
    var el = e.target.closest && e.target.closest(RIPPLE_TARGETS);
    if (!el || el.disabled) return;
    if (el.closest(NO_RIPPLE) || el.matches(NO_RIPPLE)) return;
    var r = el.getBoundingClientRect();
    // Keyboard activation reports 0,0 — centre the wave in that case.
    var x = e.clientX ? e.clientX - r.left : r.width / 2;
    var y = e.clientY ? e.clientY - r.top : r.height / 2;
    var size = Math.max(r.width, r.height) * 1.1;
    var span = document.createElement('span');
    span.className = 'ripple';
    span.style.width = span.style.height = size + 'px';
    span.style.left = (x - size / 2) + 'px';
    span.style.top = (y - size / 2) + 'px';
    el.appendChild(span);
    setTimeout(function () { span.remove(); }, 600);
  }

  /* ── Keyboard press ──
     :active never fires for space/enter, so the button would light up on
     hover but stay rigid when a keyboard user actually triggered it. */
  function keyPress(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var el = document.activeElement;
    if (!el || !el.matches || !el.matches(RIPPLE_TARGETS)) return;
    el.classList.add('is-press');
    setTimeout(function () { el.classList.remove('is-press'); }, 130);
  }

  /* ── Countdown digits ──
     The countdown is written by an inline script inside each ticket section,
     so rather than reach into it we watch the digits and animate whichever
     one actually changed. A digit that did not change stays still, which is
     what makes the seconds column read as a clock rather than a strobe. */
  function watchDigits() {
    if (!('MutationObserver' in window)) return;
    var digits = document.querySelectorAll('.cd-num, .countdown__number');
    if (!digits.length) return;
    var obs = new MutationObserver(function (records) {
      if (REDUCED.matches) return;
      records.forEach(function (rec) {
        var el = rec.target.nodeType === 3 ? rec.target.parentNode : rec.target;
        if (!el || !el.classList || !el.classList.contains('cd-num')) {
          if (!el || !el.classList || !el.classList.contains('countdown__number')) return;
        }
        el.classList.remove('is-tick');
        void el.offsetWidth;            // restart the animation
        el.classList.add('is-tick');
      });
    });
    Array.prototype.forEach.call(digits, function (d) {
      obs.observe(d, { childList: true, characterData: true, subtree: true });
    });
  }

  /* -- In-page travel --
     Every "#section" link in the theme used to carry its own inline onclick
     calling scrollIntoView, which handed the trip to the browser's default
     smooth scroll: one fixed speed whatever the distance, no way to call it
     off once it started, and no idea the sticky header was sitting on the
     landing spot. This owns all of it instead. Links keep their href, so
     without JS they still jump the old way. */
  var HEADER_GAP = 16;          // breathing room under the sticky header
  var trip = null;

  function headerOffset() {
    var h = document.querySelector('.header');
    return (h ? h.getBoundingClientRect().height : 0) + HEADER_GAP;
  }

  function stopTrip() {
    if (!trip) return;
    cancelAnimationFrame(trip.raf);
    document.documentElement.style.scrollBehavior = trip.behavior;
    trip = null;
  }

  // Slow at both ends, quickest through the middle, so the page reads as
  // setting off and arriving rather than teleporting.
  function easeInOut(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function scrollToY(y, done) {
    var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    y = Math.max(0, Math.min(Math.round(y), max));
    var from = window.pageYOffset;
    var dist = y - from;
    stopTrip();
    if (REDUCED.matches || Math.abs(dist) < 2) {
      window.scrollTo(0, y);
      if (done) done();
      return;
    }
    // Longer trips take longer, but not in proportion -- the full page would
    // be a bus ride. Clamped so a nudge still feels immediate and the longest
    // journey on the site stays under a second.
    var dur = Math.max(320, Math.min(880, Math.abs(dist) * 0.55));
    // CSS `scroll-behavior: smooth` would race this loop for the same scroll
    // position, so it is parked until the trip is over.
    trip = { raf: 0, behavior: document.documentElement.style.scrollBehavior };
    document.documentElement.style.scrollBehavior = 'auto';
    // Read it back to force the style flush. Without this the very first
    // scrollTo below can still be read as smooth, and the browser's own
    // animation then races this one for the same scroll position.
    void window.getComputedStyle(document.documentElement).scrollBehavior;
    var start = null;
    function step(now) {
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / dur);
      window.scrollTo(0, from + dist * easeInOut(t));
      if (t < 1 && trip) { trip.raf = requestAnimationFrame(step); return; }
      stopTrip();
      if (done) done();
    }
    trip.raf = requestAnimationFrame(step);
  }

  // The seat map runs its own centred jump; let it borrow the same easing.
  window.themeScrollTo = scrollToY;

  // Taking the wheel, a finger or an arrow key cancels the trip. Fighting a
  // scroll animation for control of the page is the worst part of them.
  var NAV_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar'];
  function releaseTrip(e) {
    if (!trip) return;
    if (e.type === 'keydown' && NAV_KEYS.indexOf(e.key) === -1) return;
    stopTrip();
  }

  function targetY(el) {
    var top = el.getBoundingClientRect().top + window.pageYOffset;
    // A section states its own clearance in CSS (scroll-margin-top: 96px);
    // anything else is measured against the header as it stands right now.
    var declared = parseFloat(window.getComputedStyle(el).scrollMarginTop);
    return top - (declared > 0 ? declared : headerOffset());
  }

  function arrive(el) {
    if (REDUCED.matches) return;
    el.classList.remove('is-arrive');
    void el.offsetWidth;                             // restart the animation
    el.classList.add('is-arrive');
    setTimeout(function () { el.classList.remove('is-arrive'); }, 1000);
  }

  // A hash carried in from another page: the browser has already jumped, and
  // it has jumped too far, tucking the heading under the sticky header. Put it
  // where an in-page click would have put it.
  function landOnHash() {
    if (!location.hash || location.hash.length < 2) return;
    var target = document.getElementById(location.hash.slice(1));
    if (!target) return;

    // Measured too early this lands at 65px instead of 5405: at DOM-ready the
    // poster has no intrinsic size yet and the display face has not swapped
    // in, so every section is still stacked near the top of the document and
    // the offset we would read is meaningless. Wait for load, then for the
    // fonts, and give up entirely the moment the visitor scrolls themselves --
    // being yanked away from where you just moved to is worse than landing
    // slightly low.
    var cancelled = false;
    var release = function () { cancelled = true; };
    window.addEventListener('wheel', release, { passive: true, once: true });
    window.addEventListener('touchstart', release, { passive: true, once: true });
    window.addEventListener('keydown', release, { once: true });

    var place = function () {
      if (cancelled) return;
      // The same parking scrollToY does. With `scroll-behavior: smooth` still
      // in force this jump is handed to the browser as an animation, which it
      // then abandons partway -- the symptom was landing at 65px instead of
      // 5388. Read the property back to force the style flush before moving.
      var prev = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      void window.getComputedStyle(document.documentElement).scrollBehavior;
      window.scrollTo(0, targetY(target));
      document.documentElement.style.scrollBehavior = prev;
      arrive(target);
    };

    var settle = function () {
      if (cancelled) return;
      if (document.hidden) {
        // Opened in a background tab: rAF is paused, so waiting for a frame
        // waits forever and the visitor arrives to an unscrolled page. Layout
        // is still computed while hidden, so measure and place immediately.
        place();
        return;
      }
      // Two frames: the first lets the load-time layout flush, the second
      // measures a page that has stopped moving.
      requestAnimationFrame(function () {
        requestAnimationFrame(place);
      });
    };
    var afterFonts = function () {
      if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
        document.fonts.ready.then(settle);
      } else {
        settle();
      }
    };
    if (document.readyState === 'complete') afterFonts();
    else window.addEventListener('load', afterFonts);
  }

  function onNavClick(e) {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest && e.target.closest('a[href]');
    if (!a || a.target === '_blank' || a.hasAttribute('download')) return;

    var href = a.getAttribute('href');
    var hash = '';
    var toTop = false;
    if (href.charAt(0) === '#') {
      if (href.length < 2) return;
      hash = href;
    } else if (a.origin === location.origin && a.pathname === location.pathname) {
      // Already standing on this page. A hash rides down to the section; no
      // hash means Home or the logo, so ride back up instead of refetching.
      if (a.hash && a.hash.length > 1) hash = a.hash;
      else toTop = true;
    } else {
      return;
    }

    var target = toTop ? null : document.getElementById(hash.slice(1));
    if (!toTop && !target) return;                   // unknown anchor: let the browser try
    e.preventDefault();

    var run = function () {
      scrollToY(toTop ? 0 : targetY(target), function () {
        if (target) arrive(target);
      });
      if (history.replaceState) {
        history.replaceState(null, '', toTop ? location.pathname + location.search : hash);
      }
    };

    // On mobile the link lives in the overlay, which has to get out of the
    // way before there is anything to watch.
    if (a.closest('.nav-overlay') && typeof window.closeMenu === 'function') {
      window.closeMenu();
      setTimeout(run, 260);
    } else {
      run();
    }
  }

  /* -- The nav keeps up with you --
     The underline the nav links grow on hover doubles as a position marker:
     whichever section the header is currently sitting over keeps a dimmed
     one, so the nav answers "where am I" without being asked. */
  function initNavSpy() {
    var nav = document.querySelector('.header-nav');
    if (!nav) return;
    var links = [];
    nav.querySelectorAll('a[href^="#"]').forEach(function (a) {
      var el = document.getElementById(a.getAttribute('href').slice(1));
      if (el) links.push({ a: a, el: el });
    });
    if (!links.length) return;
    var home = nav.querySelector('a:not([href^="#"])');
    var queued = 0;

    function mark() {
      queued = 0;
      var line = window.pageYOffset + headerOffset() + 8;
      var current = null, best = -Infinity;
      links.forEach(function (l) {
        var top = l.el.getBoundingClientRect().top + window.pageYOffset;
        if (top <= line && top > best) { best = top; current = l; }
      });
      links.forEach(function (l) { l.a.classList.toggle('is-current', l === current); });
      if (home) home.classList.toggle('is-current', !current);
    }

    window.addEventListener('scroll', function () {
      // Replace the pending frame instead of declining to book one. The
      // shorter `if (queued) return` latches for good if a frame is ever
      // requested and never delivered -- which is what a browser does to a
      // background tab -- leaving the nav stuck on the last section it saw.
      if (queued) cancelAnimationFrame(queued);
      queued = requestAnimationFrame(mark);
    }, { passive: true });
    window.addEventListener('resize', mark);
    mark();
  }

  /* The add-to-cart button used to scale up when its label changed. Removed:
     adding a ticket is a transaction, and a bouncing button is the register
     making a noise about it. The label change and the page move say it. */

  function init() {
    wrapArrows(document);
    watchDigits();
    document.addEventListener('pointerdown', ripple, true);
    document.addEventListener('keydown', keyPress, true);
    document.addEventListener('click', onNavClick);
    landOnHash();
    window.addEventListener('wheel', releaseTrip, { passive: true });
    window.addEventListener('touchstart', releaseTrip, { passive: true });
    window.addEventListener('keydown', releaseTrip);
    initNavSpy();
    // Language switches and the theme editor re-render copy; re-wrap after.
    document.addEventListener('lang:change', function () {
      document.querySelectorAll('[data-arw-bound]').forEach(function (el) {
        delete el.dataset.arwBound;
      });
      setTimeout(function () { wrapArrows(document); }, 0);
    });
    document.addEventListener('shopify:section:load', function (e) {
      wrapArrows(e.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
